import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ai, studies } from '@/api/endpoints';
import type { AiDraft, StudySummary } from '@/types';
import { Icon } from '@/components/Icon';
import { TiptapEditor } from '@/components/TiptapEditor';
import { MarkdownView, markdownToHtml } from '@/components/MarkdownView';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Domyślny fallback sekcji — używany przed wczytaniem listy z backendu
 * (per typ opracowania). Realny zestaw przychodzi z {@code /api/ai/sections/{studyType}}.
 */
const DOMYSLNE_SEKCJE = [
  'Wstęp',
  'Metodyka badań',
  'Lokalizacja',
  'Warunki geologiczne',
  'Warunki hydrogeologiczne',
  'Ocena zasobów',
  'Jakość wód',
  'Wnioski końcowe',
];

type Zrodlo = { plik: string; str?: string; sim?: number };

function parseZrodla(json?: string): Zrodlo[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((z): z is Record<string, unknown> => z !== null && typeof z === 'object')
      .filter(z => typeof z.plik === 'string' && (z.plik as string).length > 0)
      .map(z => ({
        plik: z.plik as string,
        str: typeof z.str === 'string' ? z.str : undefined,
        sim: typeof z.sim === 'number' ? z.sim : undefined,
      }));
  } catch { /* fall through */ }
  return [];
}

function Spinner({ className = '' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );
}

export function AiEditor() {
  const [params, setParams] = useSearchParams();
  const studyId = params.get('studyId') ? Number(params.get('studyId')) : undefined;
  const draftId = params.get('draftId') ? Number(params.get('draftId')) : undefined;

  const [oprList, setOprList] = useState<StudySummary[]>([]);
  const [sekcje, setSekcje] = useState<string[]>(DOMYSLNE_SEKCJE);
  const [section, setSection] = useState<string>(DOMYSLNE_SEKCJE[0]);
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [edited, setEdited] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ragOpen, setRagOpen] = useState(false);

  useEffect(() => { studies.list().then(setOprList).catch(() => { /* nonfatal */ }); }, []);

  // Tylko-do-odczytu gdy opracowanie jest zakończone/anulowane (parytet z StudyDetail).
  const currentStudy = oprList.find(o => o.id === studyId);
  // AWAITING_REVIEW (czeka na decyzję urzędu) — drafty zablokowane do czasu rejestracji
  // decyzji w StudyDetail. REVISION_NEEDED (urząd zwrócił z poprawkami) — drafty WRÓCIŁY
  // do edycji, więc traktujemy normalnie (nie readOnly).
  const isReadOnly = currentStudy?.status === 'COMPLETED'
    || currentStudy?.status === 'CANCELLED'
    || currentStudy?.status === 'AWAITING_REVIEW';

  // Wczytaj sekcje per typ opracowania — analiza ryzyka ma 10 sekcji, dok. hydrogeol. 8.
  // Gdy user zmieni opracowanie na inny typ, lista sekcji się aktualizuje, a aktualnie wybrana
  // sekcja jest „przyciąganiem" do tej z nowej listy (np. „Wstęp" jest w obu).
  useEffect(() => {
    if (!currentStudy?.type) {
      setSekcje(DOMYSLNE_SEKCJE);
      return;
    }
    ai.sectionsForType(currentStudy.type)
      .then(list => {
        const safeList = list.length > 0 ? list : DOMYSLNE_SEKCJE;
        setSekcje(safeList);
        // Jeśli aktualnie wybrana sekcja nie jest w nowej liście, weź pierwszą z nowej.
        if (!safeList.includes(section)) setSection(safeList[0]);
      })
      .catch(() => setSekcje(DOMYSLNE_SEKCJE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudy?.type]);

  useEffect(() => {
    if (!studyId || !draftId) { setDraft(null); setEdited(''); return; }
    setErr(null);
    studies.drafts(studyId)
      .then(drafts => {
        const d = drafts.find(x => x.id === draftId) ?? null;
        setDraft(d);
        // TiptapEditor oczekuje HTML; AI zwraca markdown ze znacznikami "##", "**" itd.
        // Konwertujemy markdown→HTML, jeśli editedText to już HTML (zaczyna się od "<"),
        // markdownToHtml przepuszcza bez zmian.
        setEdited(markdownToHtml(d?.editedText ?? d?.draftText ?? ''));
        if (d) setSection(d.section);
      })
      .catch(e => setErr(String(e)));
  }, [studyId, draftId]);

  const setParam = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const onGenerate = () => {
    if (!studyId) { setErr('Wybierz opracowanie'); return; }
    setBusy(true);
    setErr(null);
    ai.generate(studyId, section)
      .then(d => {
        setDraft(d);
        setEdited(markdownToHtml(d.editedText ?? d.draftText ?? ''));
        setParam({ studyId: String(studyId), draftId: String(d.id) });
      })
      .catch(e => setErr(String(e)))
      .finally(() => setBusy(false));
  };

  const onAccept = () => {
    if (!draft) return;
    setBusy(true);
    setErr(null);
    ai.accept(draft.id, edited)
      .then(updated => setDraft(updated))
      .catch(e => setErr(String(e)))
      .finally(() => setBusy(false));
  };

  const onUnaccept = () => {
    if (!draft) return;
    if (!confirm(`Cofnąć akceptację draftu „${draft.section}"? Wróci do edycji.`)) return;
    setBusy(true);
    setErr(null);
    ai.unaccept(draft.id)
      .then(updated => setDraft(updated))
      .catch(e => setErr(String(e)))
      .finally(() => setBusy(false));
  };

  const onRegenerate = () => {
    if (!draft) return;
    setBusy(true);
    setErr(null);
    ai.regenerate(draft.id, undefined)
      .then(() => {
        if (studyId && draft) {
          return studies.drafts(studyId).then(list => {
            const updated = list.find(x => x.id === draft.id) ?? null;
            setDraft(updated);
            setEdited(markdownToHtml(updated?.editedText ?? updated?.draftText ?? ''));
          });
        }
      })
      .catch(e => setErr(String(e)))
      .finally(() => setBusy(false));
  };

  const onKopiuj = () => { if (draft) setEdited(markdownToHtml(draft.draftText)); };

  const zrodla = parseZrodla(draft?.sourcesJson);

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-4">
        <div>
          <h1 className="flex items-center gap-2">
            <div className="sdot sdot-ai sdot-live"/>
            Edytor AI
          </h1>          <div className="text-ink-400 text-[13px]">Generowanie i edycja draftów sekcji dokumentacji</div>
        </div>
      </header>

      <div className="card mb-4">
        <div className="card-body flex flex-wrap items-end gap-3">
          <label className="field">
            <span className="field-label">Opracowanie</span>
            <SearchableSelect
              value={studyId == null ? '' : String(studyId)}
              onChange={v => setParam({ studyId: v, draftId: undefined })}
            >
              <option value="">— wybierz —</option>
              {oprList.map(o => <option key={o.id} value={o.id}>{o.number} — {o.title}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Sekcja</span>
            <SearchableSelect value={section} onChange={setSection}>
              {sekcje.map(s => <option key={s} value={s}>{s}</option>)}
            </SearchableSelect>
          </label>
          <button disabled={busy || !studyId || isReadOnly} onClick={onGenerate} className="btn btn-ai"
                  title={isReadOnly ? 'Opracowanie zakończone — generowanie zablokowane' : undefined}>
            {busy
              ? <><Spinner className="w-3.5 h-3.5"/> Generuję…</>
              : <><Icon name="sparkles" className="w-3.5 h-3.5"/> Wygeneruj draft</>}
          </button>
        </div>
      </div>

      {isReadOnly && (
        <div className="mb-4 px-4 py-3 rounded-md border-l-[4px] border-amber-500 bg-amber-50 flex items-center gap-3">
          <Icon name="check" className="w-4 h-4 text-amber-700"/>
          <div className="flex-1 text-[13px] text-amber-900">
            <div className="font-semibold">
              {currentStudy?.status === 'COMPLETED'
                ? 'Opracowanie zakończone — tryb tylko do odczytu'
                : currentStudy?.status === 'CANCELLED'
                ? 'Opracowanie anulowane — tryb tylko do odczytu'
                : 'Opracowanie czeka na decyzję urzędu — drafty zablokowane'}
            </div>
            <div className="text-[12px] text-amber-700">
              {currentStudy?.status === 'AWAITING_REVIEW'
                ? 'Aby edytować drafty po informacji od urzędu, zarejestruj decyzję w karcie opracowania (akcja „Wymaga poprawek").'
                : 'Generowanie i edycja draftów AI są zablokowane. Możesz tylko przeglądać istniejącą zawartość.'}
            </div>
          </div>
        </div>
      )}

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className={'grid gap-4 transition-[grid-template-columns] duration-200 '
                       + (ragOpen ? 'grid-cols-3' : 'grid-cols-[1fr_1fr_44px]')}>
        <div className="card bg-ai-500/5 border-ai-500/20">
          <div className="card-head">
            <span className="pill pill-ai">AI · Draft</span>
            {busy && (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-ai-600">
                <Spinner className="w-3 h-3"/> Generuję sekcję „{section}"…
              </span>
            )}
            {!busy && draft && <span className="ml-auto text-[10px] text-ink-400 font-mono">{draft.model}</span>}
          </div>
          <div className="card-body">
            {busy && (
              <div className="space-y-2 animate-pulse" aria-label="Generowanie w toku">
                <div className="h-3 bg-ai-500/15 rounded w-11/12"/>
                <div className="h-3 bg-ai-500/15 rounded w-10/12"/>
                <div className="h-3 bg-ai-500/15 rounded w-9/12"/>
                <div className="h-3 bg-ai-500/15 rounded w-11/12"/>
                <div className="h-3 bg-ai-500/15 rounded w-7/12"/>
                <div className="text-[11px] text-ink-400 mt-2">
                  Wywołuję model AI z kontekstem RAG — zwykle zajmuje 10–30 s.
                </div>
              </div>
            )}
            {!busy && !draft && <div className="text-ink-400 text-[12px]">Wygeneruj draft, aby zobaczyć podpowiedź.</div>}
            {!busy && draft && (
              <MarkdownView source={draft.draftText} className="text-[12px]"/>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">Edycja</div>
            <div className="flex gap-2 ml-auto">
              {!isReadOnly && draft?.status === 'ACCEPTED' && (
                <button disabled={busy} onClick={onUnaccept} className="btn btn-secondary btn-sm" title="Cofa akceptację — draft wraca do edycji">
                  <Icon name="x" className="w-3 h-3"/> Cofnij akceptację
                </button>
              )}
              {!isReadOnly && draft?.status !== 'ACCEPTED' && (
                <>
                  <button disabled={busy || !draft} onClick={onKopiuj} className="btn btn-secondary btn-sm">Kopiuj ←</button>
                  <button disabled={busy || !draft} onClick={onRegenerate} className="btn btn-secondary btn-sm">Regeneruj</button>
                  <button disabled={busy || !draft} onClick={onAccept} className="btn btn-primary btn-sm">Zaakceptuj</button>
                </>
              )}
              {isReadOnly && (
                <span className="text-[11px] text-amber-700 italic self-center">edycja zablokowana</span>
              )}
            </div>
          </div>
          <div className="card-body">
            {draft?.status === 'ACCEPTED' && (
              <div className="mb-3 p-2.5 border border-green-200 bg-green-50 rounded text-[12px] text-green-900 flex items-start gap-2">
                <Icon name="check" className="w-4 h-4 shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold">🔒 Draft zatwierdzony — edycja zablokowana</div>
                  {draft.acceptedByName && (
                    <div className="text-[11px] mt-0.5">
                      Zatwierdził: <strong>{draft.acceptedByName}</strong>
                      {draft.acceptedAt && <span> · {new Date(draft.acceptedAt).toLocaleString('pl-PL')}</span>}
                    </div>
                  )}
                  <div className="text-[11px] mt-0.5 text-green-800">
                    Aby ponownie edytować lub regenerować — kliknij „Cofnij akceptację" w prawym górnym rogu.
                  </div>
                </div>
              </div>
            )}
            <TiptapEditor
              value={edited}
              onChange={setEdited}
              minHeight="450px"
              readOnly={draft?.status === 'ACCEPTED' || isReadOnly}
            />
          </div>
        </div>

        {ragOpen ? (
          <div className="card">
            <div className="card-head">
              <div className="card-title">Źródła (RAG)</div>
              {zrodla.length > 0 && (
                <span className="ml-2 text-[11px] text-ink-400 font-mono">{zrodla.length}</span>
              )}
              <button onClick={() => setRagOpen(false)}
                      className="ml-auto text-ink-400 hover:text-ink-700 p-1"
                      title="Zwiń panel źródeł RAG">
                <Icon name="chevron" className="w-3 h-3"/>
              </button>
            </div>
            <div className="card-body">
              {busy ? (
                <div className="space-y-2 animate-pulse" aria-label="Pobieranie źródeł RAG">
                  {[0,1,2].map(i => (
                    <div key={i} className="flex items-start gap-2 py-1.5">
                      <div className="w-3.5 h-3.5 rounded bg-ai-500/15 mt-0.5 shrink-0"/>
                      <div className="flex-1 space-y-1">
                        <div className="h-2.5 bg-ai-500/15 rounded w-3/4"/>
                        <div className="h-2 bg-ai-500/10 rounded w-1/2"/>
                      </div>
                    </div>
                  ))}
                </div>
              ) : zrodla.length === 0 ? (
                <div className="text-[11px] text-ink-400 italic py-1">
                  {draft ? 'Brak trafień RAG dla tej sekcji.' : 'Wygeneruj sekcję, aby zobaczyć użyte źródła.'}
                </div>
              ) : zrodla.map((z, i) => (
                <div key={i} className="ai-cite flex items-start gap-2 py-1.5 border-b border-ink-100 last:border-b-0 text-[12px]">
                  <Icon name="file-text" className="w-3.5 h-3.5 text-ai-600 mt-0.5 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-ink-800 truncate">{z.plik}</div>
                    {z.str && <div className="text-[10px] text-ink-400">{z.str}</div>}
                  </div>
                  {typeof z.sim === 'number' && (
                    <span className="font-mono text-[10px] text-ai-600">{(z.sim * 100).toFixed(0)}%</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setRagOpen(true)}
            className="card hover:border-ai-500 hover:bg-ai-500/5 transition cursor-pointer flex flex-col items-center py-3 gap-2 group"
            title={'Pokaż źródła RAG' + (zrodla.length > 0 ? ' (' + zrodla.length + ' trafień)' : '')}
          >
            <Icon name="chevron" className="w-3 h-3 text-ink-500 group-hover:text-ai-600 rotate-180"/>
            <span
              className="text-[10px] uppercase tracking-wider text-ink-600 group-hover:text-ai-700 font-semibold whitespace-nowrap"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              Źródła RAG{zrodla.length > 0 && ' · ' + zrodla.length}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
